import { flatMapDeep } from 'lodash'
import request from '@/modules/base/util/request'
import globalConfig from '@/config'
import config from '@/modules/banking/config'
import lang from '@/lang'
import {
  createFormDataReducer,
  fetchLatestTurnoverForBankAccount,
  pollTransaction,
} from './_helpers'
import { fetchBankAccountList, selectBankAccount } from './bankAccountList'
import { BankingService } from '@/modules/banking/services'
import { errorCodes } from '@/modules/base/util/constants'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { generateProcessId, getProcessIdHeader } from '@/modules/banking/_helpers/generateProcessId'

const { urls } = config

// #region Additional states
export const SETUP_STATE = {
  INIT: null,
  INIT_UPDATE: 'clientInitUpdate',
  SUCCESS: 'success',
  ERROR: 'error',
  CONTACT_SETUP: 'accessData',
  SELECT_BANK_ACCOUNT: 'selectBankAccount',
  UPDATE_BANK_ACCOUNTS: 'updateBankAccounts',
}
export const BANKACCOUNT_STATE = {
  INIT: null,
  ERROR: 'error',
  LOADING: 'loading',
  SUCCESS: 'success',
}
// #endregion

export const SETUP_TYPE = {
  CREDITCARD: 'creditcard',
  ONLINE: 'online',
  PAYPAL: 'paypal',
  CASHBOX: 'cashbox',
  OFFLINE: 'offline',
}

/**
 * This is a client only state
 * It is used to choose between a ContactSetup process initialized to add a new
 * bank account (default)
 * Or to update/reinitialize an existing one because e.g. the bank institiute
 * changed its security policy
 *
 * @type {Readonly<{UPDATE: string, DEFAULT: string}>}
 */
export const CLIENT_SETUP_MODE = Object.freeze({
  DEFAULT: 'default',
  UPDATE: 'update',
})

// #region Actions
const ACTION_PREFIX = 'banking/bankAccountSetup'
const RESET_STATE = `${ACTION_PREFIX}/resetState`
const SHOW_LOADER = `${ACTION_PREFIX}/showLoader`
const HIDE_LOADER = `${ACTION_PREFIX}/hideLoader`
const ENABLE_SUBMIT_BUTTON = `${ACTION_PREFIX}/enableSubmitButton`
const DISABLE_SUBMIT_BUTTON = `${ACTION_PREFIX}/disableSubmitButton`
const OPEN_BANKACCOUNT_SETUP_MODAL = `${ACTION_PREFIX}/openModal`
const CLOSE_BANKACCOUNT_SETUP_MODAL = `${ACTION_PREFIX}/closeModal`
const OPEN_BANKACCOUNT_SETUP_VIEW = `${ACTION_PREFIX}/openView`
const CLOSE_BANKACCOUNT_SETUP_VIEW = `${ACTION_PREFIX}/closeView`
const OPEN_BANKACCOUNT_SETUP_PIN = `${ACTION_PREFIX}/openPin`
const CLOSE_BANKACCOUNT_SETUP_PIN = `${ACTION_PREFIX}/closePin`
const SUCCESS_BANKACCOUNT_SETUP_PIN = `${ACTION_PREFIX}/successPin`
const ERROR_BANKACCOUNT_SETUP_PIN = `${ACTION_PREFIX}/errorPin`
const ABORT_BANKACCOUNT_SETUP_PIN = `${ACTION_PREFIX}/abortPin`
const OPEN_BANKACCOUNT_SHOP_REQUIRED = `${ACTION_PREFIX}/openShopRequired`
const OPEN_BANKACCOUNT_SETUP_TAN_UNIVERSAL = `${ACTION_PREFIX}/openTanUniversal`
const CLOSE_BANKACCOUNT_SETUP_TAN_UNIVERSAL = `${ACTION_PREFIX}/closeTanUniversal`
const SUCCESS_BANKACCOUNT_SETUP_TAN_UNIVERSAL = `${ACTION_PREFIX}/successTanUniversal`
const ERROR_BANKACCOUNT_SETUP_TAN_UNIVERSAL = `${ACTION_PREFIX}/errorTanUniversal`
const ABORT_BANKACCOUNT_SETUP_TAN_UNIVERSAL = `${ACTION_PREFIX}/abortTanUniversal`
const OPEN_BANKACCOUNT_SETUP_TAN_INPUT = `${ACTION_PREFIX}/openTanInput`
const CLOSE_BANKACCOUNT_SETUP_TAN_INPUT = `${ACTION_PREFIX}/closeTanInput`
const SUCCESS_BANKACCOUNT_SETUP_TAN_INPUT = `${ACTION_PREFIX}/successTanInput`
const ERROR_BANKACCOUNT_SETUP_TAN_INPUT = `${ACTION_PREFIX}/errorTanInput`
const ABORT_BANKACCOUNT_SETUP_TAN_INPUT = `${ACTION_PREFIX}/abortTanInput`
const SET_SETUP_ENDPOINT = `${ACTION_PREFIX}/setSetupEndpoint`
const SET_SETUP_STATE = `${ACTION_PREFIX}/setSetupState`
const SET_SETUP_TYPE = `${ACTION_PREFIX}/setSetupType`
const SET_BANKACCOUNTS = `${ACTION_PREFIX}/setBankAccounts`
const SET_BANK_DATA = `${ACTION_PREFIX}/setBankData`
const UPDATE_BANKACCOUNT_PROPS = `${ACTION_PREFIX}/updateBankAccountProps`
const SET_BANKACCOUNT_SETUP_CONTACTID = `${ACTION_PREFIX}/setContactId`
const SET_BANKACCOUNT_SETUP_TRANSACTION = `${ACTION_PREFIX}/setTransaction`
const FINISH_BANKACCOUNT_SETUP = `${ACTION_PREFIX}/finish`
const CREATE_FORM_DATA = `${ACTION_PREFIX}/createFormData`
const CLEAR_FORM_DATA = `${ACTION_PREFIX}/clearFormData`
const SET_FORM_DATA_VALUE = `${ACTION_PREFIX}/setFormDataValue`
const SET_FORM_ERRORS = `${ACTION_PREFIX}/setFormErrors`
const SHOW_ERROR = `${ACTION_PREFIX}/showError`
const SHOW_SUCCESS = `${ACTION_PREFIX}/showSuccess`
const SET_MODAL_TITLE = `${ACTION_PREFIX}/setModalTitle`
const SET_MODAL_SUBTITLE = `${ACTION_PREFIX}/setModalSubtitle`
const SET_PROCESS_ID = `${ACTION_PREFIX}/setProcessId`
const DEACTIVATE_SHOW_BANKACCOUNT_SETUP_TAN_UNIVERSAL = `${ACTION_PREFIX}/deactivateShowBankaccountSetupTanUniversal`
// #endregion

// #region Reducer
const initialState = {
  processId: null,
  bankAccounts: null,
  contactId: null,
  initialContactId: null,
  formData: null,
  formErrors: null,
  setupState: SETUP_STATE.INIT,
  setupType: null,
  endpoint: urls.bankingApiHost,
  clientSetupMode: CLIENT_SETUP_MODE.DEFAULT,
  shopType: null,
  showBankAccountSetupModal: false,
  showBankAccountSetupView: false,
  showBankAccountSetupPin: false,
  showBankAccountShopRequired: false,
  successBankAccountSetupPin: false,
  errorBankAccountSetupPin: undefined,
  abortBankAccountSetupPin: false,
  providePinRequestToken: undefined,
  showBankAccountSetupTanUniversal: false,
  successBankAccountSetupTanUniversal: false,
  errorBankAccountSetupTanUniversal: undefined,
  abortBankAccountSetupTanUniversal: false,
  showBankAccountSetupTanInput: false,
  successBankAccountSetupTanInput: false,
  errorBankAccountSetupTanInput: undefined,
  abortBankAccountSetupTanInput: false,
  showLoader: false,
  submitButtonDisabled: false,
  transaction: undefined,
  modalTitle: 'Neues Konto',
  modalSubtitle: lang.bankAccountSetup.modalSubtitle,
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case RESET_STATE: {
      return {
        ...initialState,
        showBankAccountSetupView: state.showBankAccountSetupView,
      }
    }
    case SHOW_LOADER:
      return {
        ...state,
        showLoader: true,
      }
    case HIDE_LOADER:
      return {
        ...state,
        showLoader: false,
      }
    case ENABLE_SUBMIT_BUTTON:
      return {
        ...state,
        submitButtonDisabled: false,
      }
    case DISABLE_SUBMIT_BUTTON:
      return {
        ...state,
        submitButtonDisabled: true,
      }
    case OPEN_BANKACCOUNT_SETUP_MODAL: {
      const { initialContactId, modalTitle, setupState, setupType, modalSubtitle, clientSetupMode } = action
      const mutatedState = {
        ...state,
        initialContactId,
        modalTitle: modalTitle || state.modalTitle,
        modalSubtitle: modalSubtitle || state.modalSubtitle,
        setupType: setupType || state.setupType,
        setupState: setupState || state.setupState,
        clientSetupMode: clientSetupMode || state.clientSetupMode,
        showBankAccountSetupView: true,
      }

      return mutatedState
    }
    case CLOSE_BANKACCOUNT_SETUP_MODAL:
      return {
        ...state,
        showBankAccountSetupView: false,
      }
    case OPEN_BANKACCOUNT_SETUP_VIEW:
      return {
        ...state,
        setupState: SETUP_STATE.INIT,
        showBankAccountSetupView: true,
      }
    case CLOSE_BANKACCOUNT_SETUP_VIEW:
      return {
        ...state,
        showBankAccountSetupView: false,
      }
    case OPEN_BANKACCOUNT_SETUP_PIN:
      return {
        ...state,
        showBankAccountSetupPin: true,
        providePinRequestToken: action.token,
      }
    case SUCCESS_BANKACCOUNT_SETUP_PIN:
      return {
        ...state,
        successBankAccountSetupPin: true,
      }
    case ERROR_BANKACCOUNT_SETUP_PIN:
      return {
        ...state,
        errorBankAccountSetupPin: action.error,
      }
    case ABORT_BANKACCOUNT_SETUP_PIN:
      return {
        ...state,
        abortBankAccountSetupPin: true,
      }
    case CLOSE_BANKACCOUNT_SETUP_PIN:
      return {
        ...state,
        showBankAccountSetupPin: false,
        successBankAccountSetupPin: false,
        providePinRequestToken: undefined,
        errorBankAccountSetupPin: undefined,
        abortBankAccountSetupPin: false,
      }
    case OPEN_BANKACCOUNT_SHOP_REQUIRED:
      return {
        ...state,
        showBankAccountShopRequired: true,
        shopType: action.shopType,
      }
    case OPEN_BANKACCOUNT_SETUP_TAN_UNIVERSAL:
      return {
        ...state,
        showBankAccountSetupTanUniversal: true,
        provideUniversalRequest: action.provideUniversalRequest,
      }
    case SUCCESS_BANKACCOUNT_SETUP_TAN_UNIVERSAL:
      return {
        ...state,
        successBankAccountSetupTanUniversal: true,
      }
    case ERROR_BANKACCOUNT_SETUP_TAN_UNIVERSAL:
      return {
        ...state,
        errorBankAccountSetupTanUniversal: action.error,
      }
    case ABORT_BANKACCOUNT_SETUP_TAN_UNIVERSAL:
      return {
        ...state,
        abortBankAccountSetupTanUniversal: true,
      }
    case DEACTIVATE_SHOW_BANKACCOUNT_SETUP_TAN_UNIVERSAL:
      return {
        ...state,
        showBankAccountSetupTanUniversal: false,
      }
    case CLOSE_BANKACCOUNT_SETUP_TAN_UNIVERSAL:
      return {
        ...state,
        showBankAccountSetupTanUniversal: false,
        successBankAccountSetupTanUniversal: false,
        provideUniversalRequest: undefined,
        errorBankAccountSetupTanUniversal: undefined,
        abortBankAccountSetupTanUniversal: false,
      }
    case OPEN_BANKACCOUNT_SETUP_TAN_INPUT:
      return {
        ...state,
        showBankAccountSetupTanInput: true,
        provideTanRequest: action.provideTanRequest,
      }
    case SUCCESS_BANKACCOUNT_SETUP_TAN_INPUT:
      return {
        ...state,
        successBankAccountSetupTanInput: true,
      }
    case ERROR_BANKACCOUNT_SETUP_TAN_INPUT:
      return {
        ...state,
        errorBankAccountSetupTanInput: action.error,
      }
    case ABORT_BANKACCOUNT_SETUP_TAN_INPUT:
      return {
        ...state,
        abortBankAccountSetupTanInput: true,
      }
    case CLOSE_BANKACCOUNT_SETUP_TAN_INPUT:
      return {
        ...state,
        showBankAccountSetupTanInput: false,
        successBankAccountSetupTanInput: false,
        provideTanRequest: undefined,
        errorBankAccountSetupTanInput: undefined,
        abortBankAccountSetupTanInput: false,
      }
    case SET_SETUP_ENDPOINT: {
      const { endpoint } = action
      return {
        ...state,
        endpoint,
      }
    }
    case SET_SETUP_STATE: {
      const { setupState } = action
      return {
        ...state,
        setupState,
      }
    }
    case SET_SETUP_TYPE: {
      const { setupType } = action

      return {
        ...state,
        setupType,
      }
    }
    case SET_BANKACCOUNTS: {
      const { bankAccounts } = action
      const modalTitle = lang.bankAccountSetupSelectBankAccountsTitle
      const modalSubtitle = lang.banKAccountSetupSelectBankAccountsSubtitle

      return {
        ...state,
        bankAccounts,
        modalTitle,
        modalSubtitle,
      }
    }
    case SET_BANK_DATA: {
      const { bankData } = action
      return {
        ...state,
        bankData,
      }
    }
    case UPDATE_BANKACCOUNT_PROPS: {
      const { bankAccountId, bankAccountData: actualBankAccount } = action
      const { bankAccounts } = state

      const newBankAccounts = bankAccounts.map(bankAccount => {
        if (bankAccount.id === bankAccountId) {
          return { ...bankAccount, ...actualBankAccount }
        }
        return bankAccount
      })

      return {
        ...state,
        bankAccounts: newBankAccounts,
      }
    }
    case SET_BANKACCOUNT_SETUP_CONTACTID: {
      const { contactId } = action
      return {
        ...state,
        contactId,
        setupState: SETUP_STATE.SELECT_BANK_ACCOUNT,
      }
    }
    case SET_BANKACCOUNT_SETUP_TRANSACTION: {
      const { transaction } = action
      return {
        ...state,
        transaction,
      }
    }
    case FINISH_BANKACCOUNT_SETUP: {
      const { formData, transaction } = initialState
      return {
        ...state,
        formData,
        transaction,
        setupState: SETUP_STATE.SUCCESS,
      }
    }
    case CREATE_FORM_DATA: {
      return createFormDataReducer(state, action)
    }
    case CLEAR_FORM_DATA: {
      const { formData, formErrors } = initialState
      return {
        ...state,
        formData,
        formErrors,
      }
    }
    case SET_FORM_DATA_VALUE: {
      const { name, value } = action
      const formData = state.formData ? { ...state.formData } : {}
      const newState = { ...state, formData }
      newState.formData[name] = value

      return newState
    }
    case SET_FORM_ERRORS: {
      const { formErrors } = action
      return {
        ...state,
        formErrors,
      }
    }
    case SHOW_ERROR: {
      return {
        ...state,
        modalTitle: '',
        modalSubtitle: '',
        setupState: SETUP_STATE.ERROR,
      }
    }
    case SHOW_SUCCESS: {
      return {
        ...state,
        modalTitle: '',
        modalSubtitle: '',
        setupState: SETUP_STATE.SUCCESS,
      }
    }
    case SET_MODAL_TITLE: {
      const { modalTitle } = action
      return {
        ...state,
        modalTitle,
      }
    }
    case SET_MODAL_SUBTITLE: {
      const { modalSubtitle } = action
      return {
        ...state,
        modalSubtitle,
      }
    }
    case SET_PROCESS_ID: {
      const { processId } = action
      return {
        ...state,
        processId,
      }
    }
    default: {
      return state
    }
  }
}
// #endregion

// #region Helpers
const buildPayload = (bankAccountSetupState) => {
  const {
    formData,
    transaction: { fields },
  } = bankAccountSetupState

  const buttonId = formData.buttonId
  const store = formData.storePin
  const fieldValues = fields.map((field) => {
    const { id: fieldName, securityField } = field
    const value = formData[fieldName]

    if (fieldName.toLowerCase() === 'pin') {
      return { fieldName, securityField, value, store }
    }

    return { fieldName, securityField, value }
  })

  return {
    buttonId,
    fieldValues,
  }
}
// #endregion

// #region Action Creators
export const resetState = () => ({ type: RESET_STATE })
export const showLoader = () => ({ type: SHOW_LOADER })
export const hideLoader = () => ({ type: HIDE_LOADER })
export const enableSubmitButton = () => ({ type: ENABLE_SUBMIT_BUTTON })
export const disableSubmitButton = () => ({ type: DISABLE_SUBMIT_BUTTON })
export const openBankAccountSetupModal = () => ({
  type: OPEN_BANKACCOUNT_SETUP_MODAL,
})

export const openBankAccountUpdateSetupModal = (initialContactId) => ({
  type: OPEN_BANKACCOUNT_SETUP_MODAL,
  modalTitle: 'Änderung am Konto',
  modalSubtitle: 'Bestätige eine neue Zugangsart',
  setupState: SETUP_STATE.INIT_UPDATE,
  setupType: SETUP_TYPE.ONLINE,
  clientSetupMode: CLIENT_SETUP_MODE.UPDATE,
  initialContactId,
})
export const closeBankAccountSetupModal = () => ({ type: CLOSE_BANKACCOUNT_SETUP_MODAL })

export const openBankAccountSetupView = () => ({ type: OPEN_BANKACCOUNT_SETUP_VIEW })
export const closeBankAccountSetupView = () => ({ type: CLOSE_BANKACCOUNT_SETUP_VIEW })

export const openBankAccountSetupPin = (token) => ({ type: OPEN_BANKACCOUNT_SETUP_PIN, token })
export const successBankAccountSetupPin = () => ({ type: SUCCESS_BANKACCOUNT_SETUP_PIN })
export const closeBankAccountSetupPin = () => ({ type: CLOSE_BANKACCOUNT_SETUP_PIN })
export const errorBankAccountSetupPin = (error) => ({ type: ERROR_BANKACCOUNT_SETUP_PIN, error })
export const abortBankAccountSetupPin = () => ({ type: ABORT_BANKACCOUNT_SETUP_PIN })

export const openBankAccountShopRequired = (shopType) => ({ type: OPEN_BANKACCOUNT_SHOP_REQUIRED, shopType })

export const openBankAccountSetupTanUniversal = (provideUniversalRequest) => ({ type: OPEN_BANKACCOUNT_SETUP_TAN_UNIVERSAL, provideUniversalRequest })
export const successBankAccountSetupTanUniversal = () => ({ type: SUCCESS_BANKACCOUNT_SETUP_TAN_UNIVERSAL })
export const closeBankAccountSetupTanUniversal = () => ({ type: CLOSE_BANKACCOUNT_SETUP_TAN_UNIVERSAL })
export const errorBankAccountSetupTanUniversal = (error) => ({ type: ERROR_BANKACCOUNT_SETUP_TAN_UNIVERSAL, error })
export const abortBankAccountSetupTanUniversal = () => ({ type: ABORT_BANKACCOUNT_SETUP_TAN_UNIVERSAL })

export const openBankAccountSetupTanInput = (provideTanRequest) => ({ type: OPEN_BANKACCOUNT_SETUP_TAN_INPUT, provideTanRequest })
export const successBankAccountSetupTanInput = () => ({ type: SUCCESS_BANKACCOUNT_SETUP_TAN_INPUT })
export const closeBankAccountSetupTanInput = () => ({ type: CLOSE_BANKACCOUNT_SETUP_TAN_INPUT })
export const errorBankAccountSetupTanInput = (error) => ({ type: ERROR_BANKACCOUNT_SETUP_TAN_INPUT, error })
export const abortBankAccountSetupTanInput = () => ({ type: ABORT_BANKACCOUNT_SETUP_TAN_INPUT })

export const setSetupEndpoint = (endpoint) => ({ type: SET_SETUP_ENDPOINT, endpoint })
export const finishBankAccountSetup = () => ({ type: FINISH_BANKACCOUNT_SETUP })
export const setSetupState = (setupState) => ({ type: SET_SETUP_STATE, setupState })
export const setSetupType = (setupType) => ({ type: SET_SETUP_TYPE, setupType })
export const setBankAccounts = (bankAccounts) => ({ type: SET_BANKACCOUNTS, bankAccounts })
export const setBankData = (bankData) => ({ type: SET_BANK_DATA, bankData })
export const updateBankAccountProps = (bankAccountId, bankAccountData) => ({
  type: UPDATE_BANKACCOUNT_PROPS,
  bankAccountId,
  bankAccountData,
})
export const setBankAccountSetupContactId = (contactId) => {
  return {
    type: SET_BANKACCOUNT_SETUP_CONTACTID,
    contactId,
  }
}
export const setBankAccountSetupTransaction = (transaction) => {
  return {
    type: SET_BANKACCOUNT_SETUP_TRANSACTION,
    transaction,
  }
}
export const setBankAccountSetupTransactionAndModalProps = (transaction) => {
  const { pageDescription: modalTitle, helpText: modalSubtitle } = transaction
  return (dispatch, getState) => {
    dispatch(setBankAccountSetupTransaction(transaction))
    dispatch(setModalTitle(modalTitle))
    dispatch(setModalSubtitle(modalSubtitle))
  }
}
export const createFormData = (buttons, fields) => {
  return {
    type: CREATE_FORM_DATA,
    buttons,
    fields,
  }
}
export const clearFormData = () => ({ type: CLEAR_FORM_DATA })
export const setFormDataValue = (name, value) => {
  return {
    type: SET_FORM_DATA_VALUE,
    name,
    value,
  }
}
export const setFormErrors = (formErrors) => ({ type: SET_FORM_ERRORS, formErrors })
export const showError = () => ({ type: SHOW_ERROR })
export const showSuccess = () => ({ type: SHOW_SUCCESS })
export const setModalTitle = (modalTitle) => ({ type: SET_MODAL_TITLE, modalTitle })
export const setModalSubtitle = (modalSubtitle) => ({ type: SET_MODAL_SUBTITLE, modalSubtitle })
export const setProcessId = (processId) => ({ type: SET_PROCESS_ID, processId })
export const deactivateShowBankaccountSetupTanUniversal = () => ({ type: DEACTIVATE_SHOW_BANKACCOUNT_SETUP_TAN_UNIVERSAL })

let token
const startBankAccountSetup = (props, processId) => {
  const { contactId, bankCode, bic } = props || {}
  let requestUrl = urls.startBankAccountSetup

  if (contactId) {
    requestUrl = urls.updateBankAccountSetup(contactId)
  }

  const body = {
    redirectBackPath: globalConfig.banking.redirectBackTarget,
  }
  if (bankCode || bic) {
    body.contactSetupInitialization = { bic, bankCode }
  }

  return (dispatch) => {
    return new Promise((resolve, reject) => {
      request(requestUrl, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data: body,
        headers: {
          ...getProcessIdHeader(processId),
        },
      })
        .then(({ body: { data: transaction } }) => {
          if (transaction.endpoint || transaction.endpoint === '') {
            dispatch(setSetupEndpoint(transaction.endpoint))
          }
          return transaction
        })
        .then(({ contactSetupInfo, endpoint, transactionId, transactionState, links }) => {
          const { initToken } = contactSetupInfo
          token = initToken
          let transaction = { token: initToken }
          let system = 'bankingApi'
          if (endpoint === '') {
            if (!transactionId) {
              return {}
            }
            transaction = {
              ...transaction,
              transactionId,
              transactionState,
              links,
            }
            system = 'bookingApiSetup'
          }
          return pollTransaction(transaction, system, processId)
        })
        .then(({ contactSetupResult }) => {
          resolve(contactSetupResult)
        })
        .catch(error => {
          const { body: { meta } } = error
          reject(meta)
        })
    })
  }
}
const proceedBankAccountSetupStep = (dispatch, getState, processId) => {
  const { banking: { bankAccountSetup } } = getState()
  const { processId: processIdState } = bankAccountSetup
  const payload = buildPayload(bankAccountSetup)

  if (!processId) {
    if (!processIdState) {
      processId = generateProcessId()
      dispatch(setProcessId(processId))
    } else {
      processId = processIdState
    }
  }
  if (bankAccountSetup.endpoint === '') {
    return new Promise((resolve, reject) => {
      request(`${urls.bookingProxy}/contact-setup/data`, {
        method: 'POST',
        headers: {
          authToken: token,
          ...getProcessIdHeader(processId),
        },
        data: payload,
        auth: true,
      })
        .then(({ body: { stateToken, links } }) => {
          token = stateToken
          return pollTransaction({ token: stateToken, links }, 'bookingApiSetup', processId)
        })
        .then(({ contactSetupResult }) => {
          dispatch(setFormErrors(null))
          resolve(contactSetupResult)
        })
        .catch(error => {
          const { errorMessage } = error
          dispatch(setFormErrors([errorMessage]))
          reject(error)
        })
    })
  } else {
    return new Promise((resolve, reject) => {
      BankingService.psd2.contactSetup.postData(token, payload, processId)
        .then(({ body: { stateToken } }) => {
          return pollTransaction({ token: stateToken }, 'bankingApi', processId)
        })
        .then(({ contactSetupResult, nextToken }) => {
          if (nextToken) {
            token = nextToken
          }
          dispatch(setFormErrors(null))
          resolve(contactSetupResult)
        })
        .catch(error => {
          const { errorMessage } = error
          dispatch(setFormErrors([errorMessage]))
          reject(error)
        })
    })
  }
}

const completeBankAccountSetup = (dispatch, getState, processId) => {
  const { banking: { bankAccountSetup: { transaction: { contactId }, processId: processIdState } } } = getState()
  if (!processId) {
    if (!processIdState) {
      processId = generateProcessId()
      dispatch(setProcessId(processId))
    } else {
      processId = processIdState
    }
  }
  request(urls.completeBankAccountSetup, {
    method: 'POST',
    auth: true,
    headers: {
      ...getProcessIdHeader(processId),
    },
    isConcurrent: true,
    data: {
      contactId,
    },
  })
    .then(({ body: { data } }) => {
      const { id: contactId } = data
      dispatch(setBankAccountSetupContactId(contactId))
      fetchAvailableBankAccountsForContact(dispatch, getState, processId)
    })
    .catch(error => {
      toast.showToast({ error })
    })
}

const fetchAvailableBankAccountsForContact = (dispatch, getState, processId) => {
  dispatch(clearFormData())

  const { banking: { bankAccountSetup: { contactId }, processId: processIdState } } = getState()
  if (!processId) {
    if (!processIdState) {
      processId = generateProcessId()
      dispatch(setProcessId(processId))
    } else {
      processId = processIdState
    }
  }
  request(urls.fetchAvailableBankAccountsForContact(contactId), {
    headers: {
      ...getProcessIdHeader(processId),
    },
    auth: true,
    isConcurrent: true,
  })
    .then(({ body: { data } }) => {
      const bankAccounts = data.bankAccounts.map(bankAccount => {
        const { id, accountActive } = bankAccount

        return {
          id,
          data: bankAccount,
          transaction: {},
          activated: accountActive,
          checked: accountActive,
          disabled: accountActive,
          state: BANKACCOUNT_STATE.INIT,
        }
      })
      dispatch(setBankAccounts(bankAccounts))
      dispatch(hideLoader())
    })
    .catch(error => {
      toast.showToast({ error })
    })
}

const updateBankAccounts = (dispatch, getState, processId) => {
  const { banking: { bankAccountSetup: { bankAccounts, processId: processIdState }, bankAccountList: { bankAccountAll } } } = getState()
  const bankAccountsForUpdate = (bankAccounts || [])
    .filter(({ checked, disabled }) => (!disabled && checked))
    .map(bankAccount => {
      return {
        ...bankAccount,
        disabled: true,
      }
    })

  // step 2: update accounts
  dispatch(setSetupState(SETUP_STATE.UPDATE_BANK_ACCOUNTS))
  dispatch(disableSubmitButton())
  dispatch(setBankAccounts(bankAccountsForUpdate))

  if (!processId) {
    if (!processIdState) {
      processId = generateProcessId()
      dispatch(setProcessId(processId))
    } else {
      processId = processIdState
    }
  }

  const updateBankAccount = (data, account) => {
    const { id, checked, data: bankAccountInfo } = data
    dispatch(updateBankAccountProps(id, { state: BANKACCOUNT_STATE.LOADING }))

    const requestData = {
      id,
      accountActive: checked,
    }

    return new Promise((resolve, reject) => {
      request(urls.updateBankAccountState, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        data: requestData,
        headers: {
          ...getProcessIdHeader(processId),
        },
      })
        .then(() => {
          return fetchLatestTurnoverForBankAccount(id, bankAccountInfo, processId)
        })
        .then(() => {
          const updatedBankAccount = {
            state: BANKACCOUNT_STATE.SUCCESS,
          }
          dispatch(updateBankAccountProps(id, updatedBankAccount))
          resolve()
        })
        .catch(error => {
          toast.showToast({ bankAccountInfo, error, message: lang.defaultErrorMessage })
          if (error.body?.name === errorCodes.FINANCE_API_ERROR) {
            const errors = flatMapDeep(error.body.meta, (item) => {
              return { ...item }
            })

            dispatch(updateBankAccountProps(id, {
              state: BANKACCOUNT_STATE.ERROR,
              errors,
            }))
            return reject(errors)
          }
          return reject(error)
        })
    })
  }

  const updateBankAccountRequests = bankAccountsForUpdate.map(bankAccount => {
    return updateBankAccount(bankAccount, bankAccount)
  })
  Promise.all(updateBankAccountRequests)
    .then(() => {
    })
    .finally(() => {
      dispatch(selectBankAccount(bankAccountAll))
      dispatch(enableSubmitButton())
    })
}
export const continueBankAccountSetup = (props, processId) => {
  return (dispatch, getState) => {
    const { banking: { bankAccountSetup: { setupState, setupType, processId: processIdState } } } = getState()
    if (!processId) {
      if (!processIdState) {
        processId = generateProcessId()
        dispatch(setProcessId(processId))
      } else {
        processId = processIdState
      }
    }

    switch (setupState) {
      case SETUP_STATE.INIT_UPDATE:
      case SETUP_STATE.INIT: {
        if (!setupType) {
          throw Error('Select setup type!')
        }

        switch (setupType) {
          case SETUP_TYPE.OFFLINE:
          case SETUP_TYPE.PAYPAL: {
            dispatch(showLoader())
            dispatch(startBankAccountSetup(null, processId))
              .then((contactSetupResult) => {
                const { buttons, fields } = contactSetupResult
                const bankCode = setupType === SETUP_TYPE.PAYPAL ? '01005000' : 'OFFLINE0XXX'
                dispatch(createFormData(buttons, fields))
                dispatch(setBankAccountSetupTransaction(contactSetupResult))
                dispatch(setFormDataValue('BANK_CODE', bankCode))
                return proceedBankAccountSetupStep(dispatch, getState, processId)
              })
              .then(contactSetupResult => {
                const { buttons, fields, step, contactId } = contactSetupResult
                if (step === 'finished') {
                  // new Paypal integration
                  dispatch(setBankAccountSetupContactId(contactId))
                  dispatch(setBankAccountSetupTransactionAndModalProps(contactSetupResult))
                  completeBankAccountSetup(dispatch, getState, processId)
                } else {
                  dispatch(createFormData(buttons, fields))
                  dispatch(setSetupState(SETUP_STATE.CONTACT_SETUP))
                  dispatch(setBankAccountSetupTransactionAndModalProps(contactSetupResult))
                }
              })
              .catch(error => {
                toast.showToast({ error })
              })
              .finally(() => {
                dispatch(hideLoader())
              })
            break
          }
          default: {
            dispatch(showLoader())
            dispatch(startBankAccountSetup(props, processId))
              .then(contactSetupResult => {
                const { buttons, fields, step } = contactSetupResult
                if (step !== 'bank') {
                  const { buttons, fields, contactId } = contactSetupResult
                  if (!contactId) {
                    dispatch(createFormData(buttons, fields))
                    dispatch(setBankAccountSetupTransactionAndModalProps(contactSetupResult))
                    dispatch(setSetupState(SETUP_STATE.CONTACT_SETUP))
                    dispatch(hideLoader())
                  } else {
                    dispatch(setBankAccountSetupContactId(contactId))
                    dispatch(setBankAccountSetupTransactionAndModalProps(contactSetupResult))
                    completeBankAccountSetup(dispatch, getState, processId)
                  }
                } else {
                  dispatch(createFormData(buttons, fields))
                  dispatch(setSetupState(SETUP_STATE.CONTACT_SETUP))
                  dispatch(setBankAccountSetupTransactionAndModalProps(contactSetupResult))
                  dispatch(setFormDataValue('BANK_CODE', props.bankCode))
                  dispatch(continueBankAccountSetup(props, processId))
                }
              })
              .catch(error => {
                if (error?.shopifyShop?.length && error?.shopifyShop[0].code === errorCodes.NOT_FOUND) {
                  dispatch(openBankAccountShopRequired('shopify'))
                  dispatch(hideLoader())
                } else {
                  toast.showToast({ error })
                  throw error
                }
              })
              .finally(() => {
                dispatch(hideLoader())
              })
            break
          }
        }
        break
      }
      case SETUP_STATE.CONTACT_SETUP: {
        dispatch(showLoader())
        proceedBankAccountSetupStep(dispatch, getState, processId)
          .then(contactSetupResult => {
            const { buttons, fields, contactId } = contactSetupResult
            if (!contactId) {
              dispatch(createFormData(buttons, fields))
              dispatch(setBankAccountSetupTransactionAndModalProps(contactSetupResult))
              dispatch(hideLoader())
            } else {
              dispatch(setBankAccountSetupContactId(contactId))
              dispatch(setBankAccountSetupTransactionAndModalProps(contactSetupResult))
              completeBankAccountSetup(dispatch, getState, processId)
            }
          })
          .catch(error => {
            toast.showToast({ error })
            dispatch(hideLoader())
          })
          .finally(() => {
            dispatch(hideLoader())
          })
        break
      }
      case SETUP_STATE.SELECT_BANK_ACCOUNT: {
        return updateBankAccounts(dispatch, getState, processId)
      }
      case SETUP_STATE.UPDATE_BANK_ACCOUNTS:
      case SETUP_STATE.ERROR:
      case SETUP_STATE.SUCCESS: {
        dispatch(fetchBankAccountList())
        return dispatch(closeBankAccountSetupModal())
      }
    }
  }
}
// #endregion
